import { TourConfig, startTour, resetTour } from './TourSystem';
import PageWelcomeModal from './PageWelcomeModal';
import 'driver.js/dist/driver.css';

// PHASE 1: Hub Overview
// Helper to robustly start a tour only when the target element exists
const startTourWhenElementExists = (tourId: string, selector: string, startFunc: () => void, maxAttempts = 20) => {
    let attempts = 0;
    const checkInterval = setInterval(() => {
        attempts++;
        const element = document.querySelector(selector);

        if (element) {
            console.log(`✅ Found target element "${selector}" for tour "${tourId}". Starting tour.`);
            clearInterval(checkInterval);
            // Small delay to ensure layout is stable
            setTimeout(() => {
                resetTour(tourId);
                startFunc();
            }, 500);
        } else if (attempts >= maxAttempts) {
            console.error(`❌ Failed to find target element "${selector}" for tour "${tourId}" after ${attempts} attempts.`);
            clearInterval(checkInterval);
            // Fallback: Try to start anyway, driver.js might handle it or just fail gracefully
            resetTour(tourId);
            startFunc();
        } else {
            console.log(`⏳ Waiting for element "${selector}"... (${attempts}/${maxAttempts})`);
        }
    }, 200); // Check every 200ms
};

const threeDHubPhase1Config: TourConfig = {
    tourId: 'threedhub-phase1',
    steps: [
        {
            popover: {
                title: '🌐 Welcome to 3D Innovation Hub',
                description: 'Your gateway to immersive 3D experiences. This tour will automatically enter each of the three 3D environments and show you all their features: Virtual Office, Product Visualizer, and Project Universe. Get ready for a complete walkthrough!',
            }
        },
        {
            element: '#btn-enter-office',
            popover: {
                title: '🏢 Virtual Office Feature',
                description: 'Click this button to enter the 3D Virtual Office tour - an immersive walkthrough of the TechUnity digital headquarters. In a moment, I will automatically click it for you.',
                side: "bottom",
            }
        },
        {
            popover: {
                title: '🚪 Entering Virtual Office...',
                description: 'Taking you into the Virtual Office now. Watch as the 3D environment loads with full controls and features. Starting in 2 seconds...',
            }
        }
    ],
    onComplete: () => {
        setTimeout(() => {
            const officeBtn = document.getElementById('btn-enter-office');
            if (officeBtn) {
                officeBtn.click();
                setTimeout(() => {
                    startThreeDHubPhase2Tour();
                }, 1000);
            }
        }, 1000);
    }
};

// PHASE 2: Inside Virtual Office
const threeDHubPhase2Config: TourConfig = {
    tourId: 'threedhub-phase2',
    steps: [
        {
            element: '#office-title',
            popover: {
                title: '🏢 Inside Virtual Office',
                description: 'You are now inside the Virtual Office 3D experience. This is a full 360° walkthrough of our digital headquarters with rooms, collaborative zones, and interactive workstations.',
                side: "bottom",
            }
        },
        {
            element: '#office-canvas',
            popover: {
                title: '🎮 3D Rendering Canvas',
                description: 'This is the main 3D viewport where the virtual office environment renders. In a full implementation, you would see a WebGL-powered 3D model here. Use mouse to navigate: drag to rotate, scroll to zoom, right-click to pan.',
                side: "top",
            }
        },
        {
            element: '#office-vr-mode',
            popover: {
                title: '🕶️ VR Mode Button',
                description: 'Click this to enable VR mode. Connect an Oculus Quest, HTC Vive, or any WebXR-compatible headset for full immersive experience. Walk through the office in virtual reality with motion controllers.',
                side: "bottom",
            }
        },
        {
            element: '#office-tour-mode',
            popover: {
                title: '🎯 Guided Tour Mode',
                description: 'This button starts an automated guided tour that moves your camera through predefined waypoints in the office, highlighting key areas like conference rooms, workstations, and collaboration spaces.',
                side: "bottom",
            }
        },
        {
            element: '#office-controls',
            popover: {
                title: '⌨️ Control Reference',
                description: 'These three cards provide control guides: Mouse controls (drag/zoom/pan), Keyboard shortcuts (WASD movement, arrows for camera), and VR headset instructions (motion controller support).',
                side: "top",
            }
        },
        {
            element: '#office-back-btn',
            popover: {
                title: '⬅️ Navigation Button',
                description: 'This button returns you to the main 3D Hub. You can switch between experiences anytime. Now I will take you back to explore the next feature.',
                side: "bottom",
            }
        },
        {
            popover: {
                title: '🔄 Returning to Hub...',
                description: 'Exiting Virtual Office. Click "Done" to return to the hub and continue to the Product Visualizer.',
            }
        }
    ],
    onComplete: () => {
        setTimeout(() => {
            const backBtn = document.getElementById('office-back-btn');
            if (backBtn) {
                backBtn.click();
                setTimeout(() => {
                    startThreeDHubPhase3Tour();
                }, 2000);
            }
        }, 1000);
    }
};

// PHASE 3: Hub - Product Visualizer Card
const threeDHubPhase3Config: TourConfig = {
    tourId: 'threedhub-phase3',
    steps: [
        {
            element: '#btn-launch-visualizer',
            popover: {
                title: '📦 Product Visualizer Feature',
                description: 'Click this button to launch the Product Visualizer - a high-fidelity 3D model viewer for prototypes and products. I will automatically click it to take you inside.',
                side: "bottom",
            }
        },
        {
            popover: {
                title: '🚪 Entering Product Visualizer...',
                description: 'Loading the Product Visualizer environment. You will see a 3D viewport, material controls, and lighting options. Starting in 2 seconds...',
            }
        }
    ],
    onComplete: () => {
        setTimeout(() => {
            const visualizerBtn = document.getElementById('btn-launch-visualizer');
            if (visualizerBtn) {
                visualizerBtn.click();
                setTimeout(() => {
                    startThreeDHubPhase4Tour();
                }, 1000);
            }
        }, 1000);
    }
};

// PHASE 4: Inside Product Visualizer
const threeDHubPhase4Config: TourConfig = {
    tourId: 'threedhub-phase4',
    steps: [
        {
            element: '#visualizer-title',
            popover: {
                title: '📦 Inside Product Visualizer',
                description: 'You are now in the Product Visualizer. This tool lets you inspect 3D models in real-time with professional rendering quality. Designers use this for client presentations and design reviews.',
                side: "bottom",
            }
        },
        {
            element: '#visualizer-viewport',
            popover: {
                title: '🎨 3D Model Viewport',
                description: 'This is the main 3D viewer where your products appear. Drag to rotate the model, scroll to zoom in/out, and click on parts for annotations. Supports OBJ, FBX, GLTF, and STL file formats.',
                side: "right",
            }
        },
        {
            element: '#visualizer-upload',
            popover: {
                title: '⬆️ Upload 3D Model',
                description: 'Click here to upload your own 3D models from your computer. Supported formats: OBJ (with MTL), FBX, GLTF/GLB (PBR materials), and STL (for 3D printing). Models load instantly in the viewport.',
                side: "bottom",
            }
        },
        {
            element: '#visualizer-controls',
            popover: {
                title: '🎛️ Visualization Controls',
                description: 'This sidebar provides: Material switcher (metal, plastic, glass), Lighting intensity slider for better visibility, and a list of supported file formats. Customize how your model appears.',
                side: "left",
            }
        },
        {
            element: '#visualizer-back-btn',
            popover: {
                title: '⬅️ Back to Hub',
                description: 'Return to the main 3D Hub with this button. One more experience to explore - the Project Universe. I will take you back now.',
                side: "bottom",
            }
        },
        {
            popover: {
                title: '🔄 Returning to Hub...',
                description: 'Exiting Product Visualizer. Click "Done" to return to the hub and continue to the Project Universe.',
            }
        }
    ],
    onComplete: () => {
        setTimeout(() => {
            const backBtn = document.getElementById('visualizer-back-btn');
            if (backBtn) {
                backBtn.click();
                setTimeout(() => {
                    startThreeDHubPhase5Tour();
                }, 2000);
            }
        }, 1000);
    }
};

// PHASE 5: Hub - Project Universe Card
const threeDHubPhase5Config: TourConfig = {
    tourId: 'threedhub-phase5',
    steps: [
        {
            element: '#btn-explore-universe',
            popover: {
                title: '🌌 Project Universe Feature',
                description: 'Click this button to explore Project Universe - a 3D galaxy visualization where each project is a star. I will automatically click it to take you inside this cosmic view.',
                side: "bottom",
            }
        },
        {
            popover: {
                title: '🚪 Entering Project Universe...',
                description: 'Loading the Project Universe galaxy. See all your projects as stars in a connected universe. Entering in 2 seconds...',
            }
        }
    ],
    onComplete: () => {
        setTimeout(() => {
            const universeBtn = document.getElementById('btn-explore-universe');
            if (universeBtn) {
                universeBtn.click();
                setTimeout(() => {
                    startThreeDHubPhase6Tour();
                }, 1000);
            }
        }, 1000);
    }
};

// PHASE 6: Inside Project Universe
const threeDHubPhase6Config: TourConfig = {
    tourId: 'threedhub-phase6',
    steps: [
        {
            element: '#universe-title',
            popover: {
                title: '🌌 Inside Project Universe',
                description: 'Welcome to the Project Universe galaxy view. This unique visualization shows your entire project portfolio as an interactive 3D star field. Each node represents a project, sized by budget and colored by status.',
                side: "bottom",
            }
        },
        {
            element: '#universe-canvas',
            popover: {
                title: '🌠 Galaxy Visualization Canvas',
                description: 'This is the 3D universe viewport. Each glowing orb is a project. Connections between projects show shared team members or dependencies. Zoom in to see project details, zoom out for the big picture. Drag to navigate the galaxy.',
                side: "top",
            }
        },
        {
            element: '#universe-zoom-in',
            popover: {
                title: '🔍 Zoom In Control',
                description: 'Click to zoom closer to specific projects. Get near enough and a tooltip appears with project details: name, team size, budget, and status. Double-click a project node to open its full workspace.',
                side: "bottom",
            }
        },
        {
            element: '#universe-zoom-out',
            popover: {
                title: '🔭 Zoom Out Control',
                description: 'Pull back to see the entire project universe at once. Useful for portfolio managers overseeing dozens of projects. Identify clusters of related work and isolated initiatives.',
                side: "bottom",
            }
        },
        {
            element: '#universe-legend',
            popover: {
                title: '🎨 Project Status Legend',
                description: 'This legend explains node colors: Blue = Active projects (in progress), Green = Completed projects, Yellow = On Hold (paused), Gold = Proposals (under review). Color coding makes status instantly visible.',
                side: "top",
            }
        },
        {
            element: '#universe-back-btn',
            popover: {
                title: '⬅️ Return to Hub',
                description: 'This button takes you back to the main 3D Hub. We have finished the display experiences. Now, get ready to explore the interactive creation tools: Team Rooms, Avatar Studio, and World Builder!',
                side: "bottom",
            }
        },
        {
            popover: {
                title: '🔄 Returning to Hub...',
                description: 'We are done with the Project Universe. Click "Done" to return to the Hub and continue to the Team Rooms feature.',
            }
        }
    ],
    onComplete: () => {
        console.log("Phase 6 Complete: Transitioning to Team Rooms...");
        setTimeout(() => {
            const backBtn = document.getElementById('universe-back-btn');
            if (backBtn) {
                backBtn.click();
                // Use robust waiter instead of blind timeout
                startTourWhenElementExists('threedhub-phase11', '#3d-feature-rooms', startThreeDHubPhase11Tour);
            } else {
                console.error("Universe back button not found");
                // Fallback: try to force navigation if button missing

            }
        }, 1000);
    }
};

// PHASE 7: Hub - Collaboration Section
const threeDHubPhase7Config: TourConfig = {
    tourId: 'threedhub-phase7',
    steps: [
        {
            element: '#collab-section',
            popover: {
                title: '🤝 Collaboration Features',
                description: 'Below the three main 3D experiences, you will find two additional collaboration features: VR Session for multi-user virtual reality meetings, and Watch Demo to see all features in action via video.',
                side: "top",
            }
        },
        {
            element: '#collab-title',
            popover: {
                title: '🌐 Future Collaboration',
                description: 'This section promotes the collaborative aspect of our 3D Hub - connecting teams across the globe in immersive virtual spaces or showing them what is possible through demonstrations.',
                side: "bottom",
            }
        },
        {
            element: '#btn-start-vr',
            popover: {
                title: '🕶️ Start VR Session Button',
                description: 'Click this to launch a multi-user VR collaboration session. Connect your Oculus Quest, HTC Vive, or any WebXR device to meet with your team in a shared 3D space. I will click this for you now.',
                side: "bottom",
            }
        },
        {
            popover: {
                title: '🚪 Entering VR Session...',
                description: 'Loading the VR Session setup screen. You will see headset options and connection instructions. Starting in 2 seconds...',
            }
        }
    ],
    onComplete: () => {
        setTimeout(() => {
            const vrBtn = document.getElementById('btn-start-vr');
            if (vrBtn) {
                vrBtn.click();
                setTimeout(() => {
                    startThreeDHubPhase8Tour();
                }, 1000);
            }
        }, 1000);
    }
};

// PHASE 8: Inside VR Session
const threeDHubPhase8Config: TourConfig = {
    tourId: 'threedhub-phase8',
    steps: [
        {
            element: '#vr-title',
            popover: {
                title: '🕶️ Inside VR Collaboration Session',
                description: 'You are now in the VR Session setup screen. This feature enables immersive multi-user virtual reality meetings where team members can collaborate in a shared 3D environment.',
                side: "bottom",
            }
        },
        {
            element: '#vr-setup',
            popover: {
                title: '🎮 VR Headset Options',
                description: 'This section shows supported VR devices: Oculus Quest 2/3 (wireless), HTC Vive/Valve Index (SteamVR), and any WebXR-compatible browser-based VR. Choose your device to see specific connection instructions.',
                side: "right",
            }
        },
        {
            element: '#vr-preview',
            popover: {
                title: '👁️ VR Preview Panel',
                description: 'This panel shows a preview of what you will see in VR. Click "Connect Headset" to initiate the WebXR connection. Your browser will request permission to access your VR device.',
                side: "left",
            }
        },
        {
            element: '#vr-connect-btn',
            popover: {
                title: '🔗 Connect Headset Button',
                description: 'Clicking this triggers the WebXR API to detect and connect to your VR headset. Once connected, you enter the immersive 3D collaboration space with spatial audio and hand tracking.',
                side: "bottom",
            }
        },
        {
            element: '#vr-features',
            popover: {
                title: '✨ VR Session Features',
                description: 'Three key features: (1) Multi-User Rooms - up to 20 people simultaneously, (2) Hand Tracking - natural gestures on Quest without controllers, (3) Spatial Audio - voices come from each person\'s 3D location for realistic immersion.',
                side: "top",
            }
        },
        {
            element: '#vr-back-btn',
            popover: {
                title: '⬅️ Back to Hub',
                description: 'Return to the main 3D Hub. One more feature to explore - the Watch Demo video. I will take you back now.',
                side: "bottom",
            }
        },
        {
            popover: {
                title: '🔄 Returning to Hub...',
                description: 'Exiting VR Session setup. Click "Done" to return to the hub and continue to the Watch Demo feature.',
            }
        }
    ],
    onComplete: () => {
        setTimeout(() => {
            const backBtn = document.getElementById('vr-back-btn');
            if (backBtn) {
                backBtn.click();
                setTimeout(() => {
                    startThreeDHubPhase9Tour();
                }, 2000);
            }
        }, 1000);
    }
};

// PHASE 9: Hub - Watch Demo Button
const threeDHubPhase9Config: TourConfig = {
    tourId: 'threedhub-phase9',
    steps: [
        {
            element: '#btn-watch-demo',
            popover: {
                title: '🎬 Watch Demo Button',
                description: 'This button launches an interactive demonstration video showcasing all 3D Hub features in action: Virtual Office walkthrough, Product Visualizer with real models, and Project Universe galaxy navigation. I will click it for you.',
                side: "bottom",
            }
        },
        {
            popover: {
                title: '🚪 Loading Demo Player...',
                description: 'Opening the demo video player with chapter navigation and feature highlights. Starting in 2 seconds...',
            }
        }
    ],
    onComplete: () => {
        setTimeout(() => {
            const demoBtn = document.getElementById('btn-watch-demo');
            if (demoBtn) {
                demoBtn.click();
                setTimeout(() => {
                    startThreeDHubPhase10Tour();
                }, 1000);
            }
        }, 1000);
    }
};

// PHASE 10: Inside Watch Demo
const threeDHubPhase10Config: TourConfig = {
    tourId: 'threedhub-phase10',
    steps: [
        {
            element: '#demo-title',
            popover: {
                title: '🎬 Inside 3D Feature Demonstration',
                description: 'You are now in the Watch Demo view. This is an interactive video player showcasing all 3D Hub capabilities: Virtual Office tours, Product Visualizer demonstrations, and Project Universe navigation.',
                side: "bottom",
            }
        },
        {
            element: '#demo-player',
            popover: {
                title: '📺 Interactive Demo Video Player',
                description: 'This is the main video viewport. The demo shows real usage of all three 3D experiences with voice-over explanations. Click Play to start the 6-minute comprehensive overview.',
                side: "top",
            }
        },
        // ... (skipping some steps for brevity in replacement if possible, but better be complete)
        {
            element: '#demo-chapters',
            popover: {
                title: '📑 Video Chapters',
                description: 'Chapters allow quick navigation: (1) Virtual Office Tour, (2) Product Visualization, (3) Project Universe.',
                side: "top",
            }
        },
        {
            element: '#demo-back-btn',
            popover: {
                title: '⬅️ Back to Hub',
                description: 'We have finished the Demo. You can click here to return to the main 3D Hub.',
                side: "bottom",
            }
        },
        {
            popover: {
                title: '🎉 Tour Complete!',
                description: 'You have now explored all features including the new Team Rooms, Avatar Studio, and World Builder. Enjoy your experience!',
            }
        }
    ],
    onComplete: () => {
        // Tour Finished
    }
};

// PHASE 11: Hub - Team Rooms
const threeDHubPhase11Config: TourConfig = {
    tourId: 'threedhub-phase11',
    steps: [
        {
            element: '#3d-feature-rooms',
            popover: {
                title: '👥 Team Rooms Feature',
                description: 'This is the Team Rooms hub. Here you can create and join persistent 3D workspaces for your team. From design sprints to executive meetings, every room is a shared 3D environment.',
                side: "top",
            }
        },
        {
            popover: {
                title: '🚪 Entering Team Rooms...',
                description: 'Entering the Team Rooms dashboard to see available sessions. Starting in 2 seconds...',
            }
        }
    ],
    onComplete: () => {
        setTimeout(() => {
            const btn = document.getElementById('btn-enter-rooms');
            if (btn) {
                btn.click();
                setTimeout(() => {
                    startThreeDHubPhase12Tour();
                }, 1000);
            }
        }, 1000);
    }
};

// PHASE 12: Inside Team Rooms
const threeDHubPhase12Config: TourConfig = {
    tourId: 'threedhub-phase12',
    steps: [
        {
            element: '#rooms-title',
            popover: {
                title: '👥 Inside Team Rooms Hub',
                description: 'Welcome to the Team Rooms dashboard. This interface lists all active and archived 3D sessions available to you.',
                side: "bottom",
            }
        },
        {
            element: '#rooms-hub-container',
            popover: {
                title: '📋 Active Sessions',
                description: 'Here you see available rooms with participant counts and status. Click "Create Room" to spawn a new 3D workspace instantly, or "Join Room" to enter an existing session.',
                side: "top",
            }
        },
        {
            element: '#rooms-back-btn',
            popover: {
                title: '⬅️ Navigation',
                description: 'Let\'s go back to the hub to see the Avatar Studio, where you can customize your digital twin.',
                side: "bottom",
            }
        }
    ],
    onComplete: () => {
        setTimeout(() => {
            const btn = document.getElementById('rooms-back-btn');
            if (btn) {
                btn.click();
                setTimeout(() => {
                    startThreeDHubPhase13Tour();
                }, 2000);
            }
        }, 1000);
    }
};

// PHASE 13: Hub - Avatar Studio
const threeDHubPhase13Config: TourConfig = {
    tourId: 'threedhub-phase13',
    steps: [
        {
            element: '#3d-feature-avatar',
            popover: {
                title: '👤 Avatar Studio Feature',
                description: 'Your digital identity matters. The Avatar Studio lets you customize exactly how you appear in VR sessions and team rooms. High-fidelity avatars with facial expressions and gestures.',
                side: "top",
            }
        },
        {
            popover: {
                title: '🚪 Entering Avatar Studio...',
                description: 'Opening the Avatar Studio customization suite. Starting in 2 seconds...',
            }
        }
    ],
    onComplete: () => {
        setTimeout(() => {
            const btn = document.getElementById('btn-enter-avatar');
            if (btn) {
                btn.click();
                setTimeout(() => {
                    startThreeDHubPhase14Tour();
                }, 1000);
            }
        }, 1000);
    }
};

// PHASE 14: Inside Avatar Studio
const threeDHubPhase14Config: TourConfig = {
    tourId: 'threedhub-phase14',
    steps: [
        {
            element: '#avatar-title',
            popover: {
                title: '👤 Inside Avatar Studio',
                description: 'Customize your 3D avatar here. Changes update instantly across all 3D experiences.',
                side: "bottom",
            }
        },
        {
            element: '#avatar-studio-container',
            popover: {
                title: '🎨 Customization Suite',
                description: 'Use the controls to modify hair, face, clothing, and accessories. You can preview animations and save presets for different meeting types (formal vs casual).',
                side: "top",
            }
        },
        {
            element: '#avatar-back-btn',
            popover: {
                title: '⬅️ Navigation',
                description: 'One final stop: The World Builder. Let\'s return to the hub.',
                side: "bottom",
            }
        }
    ],
    onComplete: () => {
        setTimeout(() => {
            const btn = document.getElementById('avatar-back-btn');
            if (btn) {
                btn.click();
                setTimeout(() => {
                    startThreeDHubPhase15Tour();
                }, 2000);
            }
        }, 1000);
    }
};

// PHASE 15: Hub - World Builder
const threeDHubPhase15Config: TourConfig = {
    tourId: 'threedhub-phase15',
    steps: [
        {
            element: '#3d-feature-world',
            popover: {
                title: '🌍 World Builder Feature',
                description: 'The ultimate creativity tool. World Builder allows you to construct entire virtual environments from scratch using a drag-and-drop 3D editor.',
                side: "top",
            }
        },
        {
            popover: {
                title: '🚪 Entering World Builder...',
                description: 'Launching the World Builder engine. Prepare to create your own universe. Starting in 2 seconds...',
            }
        }
    ],
    onComplete: () => {
        setTimeout(() => {
            const btn = document.getElementById('btn-enter-world');
            if (btn) {
                btn.click();
                setTimeout(() => {
                    startThreeDHubPhase16Tour();
                }, 1000);
            }
        }, 1000);
    }
};

// PHASE 16: Inside World Builder (FINAL)
const threeDHubPhase16Config: TourConfig = {
    tourId: 'threedhub-phase16',
    steps: [
        {
            element: '#world-title',
            popover: {
                title: '🌍 Inside World Builder',
                description: 'Welcome to the World Builder. Here you can terraform landscapes, place buildings, and script interactions.',
                side: "bottom",
            }
        },
        {
            element: '#world-builder-container',
            popover: {
                title: '🏗️ Construction Canvas',
                description: 'Drag assets from the library to the scene. Use the terrain tools to sculpt mountains and rivers. Adjust lighting and weather dynamically.',
                side: "top",
            }
        },
        {
            element: '#world-back-btn',
            popover: {
                title: '⬅️ Back to Hub',
                description: 'We have explored the new features. Now, let\'s look at our advanced VR Session capabilities.',
                side: "bottom",
            }
        },
        {
            popover: {
                title: '🔄 Continuing Tour...',
                description: 'Next stop: VR Session. Click "Done" to return to the Hub...',
            }
        }
    ],
    onComplete: () => {
        setTimeout(() => {
            const btn = document.getElementById('world-back-btn');
            if (btn) {
                btn.click();
                setTimeout(() => {
                    startThreeDHubPhase7Tour();
                }, 2000);
            }
        }, 1000);
    }
};


export function startThreeDHubTour() {
    startTour(threeDHubPhase1Config);
}

function startThreeDHubPhase2Tour() {
    startTour(threeDHubPhase2Config);
}

function startThreeDHubPhase3Tour() {
    startTour(threeDHubPhase3Config);
}

function startThreeDHubPhase4Tour() {
    startTour(threeDHubPhase4Config);
}

function startThreeDHubPhase5Tour() {
    startTour(threeDHubPhase5Config);
}

function startThreeDHubPhase6Tour() {
    startTour(threeDHubPhase6Config);
}

function startThreeDHubPhase7Tour() {
    startTour(threeDHubPhase7Config);
}

function startThreeDHubPhase8Tour() {
    startTour(threeDHubPhase8Config);
}

function startThreeDHubPhase9Tour() {
    startTour(threeDHubPhase9Config);
}

function startThreeDHubPhase10Tour() {
    startTour(threeDHubPhase10Config);
}

function startThreeDHubPhase11Tour() {
    startTour(threeDHubPhase11Config);
}

function startThreeDHubPhase12Tour() {
    startTour(threeDHubPhase12Config);
}

function startThreeDHubPhase13Tour() {
    startTour(threeDHubPhase13Config);
}

function startThreeDHubPhase14Tour() {
    startTour(threeDHubPhase14Config);
}

function startThreeDHubPhase15Tour() {
    startTour(threeDHubPhase15Config);
}

function startThreeDHubPhase16Tour() {
    startTour(threeDHubPhase16Config);
}

const ThreeDHubTour = () => {
    return (
        <PageWelcomeModal
            title="Welcome to 3D Hub"
            description="Explore three immersive 3D experiences: Virtual Office, Product Visualizer, and Project Universe."
            onStartTour={startThreeDHubTour}
            tourId="threedhub-phase1"
        />
    );
};

export default ThreeDHubTour;
